import { useState } from "react";
import { Link } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import type { MessageKey } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";
import { Paginacao } from "../components/common/Paginacao.js";
import {
  CHIP_OFF,
  CHIP_ON,
  FILTER_INPUT,
  FilterField,
  FilterPanel,
} from "../components/common/FilterBar.js";

const PAGE_SIZE = 30;

const KINDS = [
  "customer_invoice",
  "invoice_reminder",
  "credit_note",
  "proforma",
  "worklog",
  "bank_statement",
  "user_invite",
  "password_reset",
  "technician_application",
  "other",
] as const;
type Kind = (typeof KINDS)[number];

const KIND_LABEL: Record<Kind, MessageKey> = {
  customer_invoice: "emailOutbox.kindCustomerInvoice",
  invoice_reminder: "emailOutbox.kindInvoiceReminder",
  credit_note: "emailOutbox.kindCreditNote",
  proforma: "emailOutbox.kindProforma",
  worklog: "emailOutbox.kindWorklog",
  bank_statement: "emailOutbox.kindBankStatement",
  user_invite: "emailOutbox.kindUserInvite",
  password_reset: "emailOutbox.kindPasswordReset",
  technician_application: "emailOutbox.kindTechnicianApplication",
  other: "emailOutbox.kindOther",
};

/**
 * F-email-outbox (CEO 2026-10-02) — "caixa de saída": histórico de todos os
 * e-mails enviados pelo Gestion (faturas, proformas, worklogs, extratos,
 * convites, senha), com destinatário, anexos, mensagem livre e falhas.
 */
export default function EmailOutbox() {
  const { user } = useAuth();
  const t = useT();
  const adminCompanyIds = user
    ? user.isSuperAdmin
      ? user.allowedCompanyIds
      : user.memberships.filter((m) => m.active && m.role === "admin").map((m) => m.companyId)
    : [];

  const [kind, setKind] = useState<Kind | "">("");
  const [status, setStatus] = useState<"" | "sent" | "failed">("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);

  const companiesQuery = trpc.companies.list.useQuery();
  const adminCompanies = (companiesQuery.data ?? []).filter((c) => adminCompanyIds.includes(c.id));
  // Mesma empresa ativa do resto do ERP (shell), não a primeira da lista.
  const [companyId, setCompanyId] = useSelectedCompany(adminCompanies);

  const listQuery = trpc.emailLog.list.useQuery(
    {
      companyId: companyId ?? 0,
      kind: kind || undefined,
      status: status || undefined,
      q: q.trim() || undefined,
      limit: PAGE_SIZE,
      offset,
    },
    { enabled: companyId !== null },
  );

  if (!user) return null;
  if (adminCompanies.length === 0) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold">{t("emailOutbox.title")}</h1>
        <p className="text-sm text-neutral-400">{t("emailOutbox.notAdmin")}</p>
      </Shell>
    );
  }

  const data = listQuery.data;
  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;

  return (
    <Shell>
      <div>
        <h1 className="text-xl font-semibold">{t("emailOutbox.title")}</h1>
        <p className="text-sm text-neutral-500">{t("emailOutbox.subtitle")}</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 grow basis-full sm:basis-0">
          <FilterPanel testId="emailOutbox-filter-panel">
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              <FilterField label={t("emailOutbox.filterCompany")}>
                <select
                  value={companyId ?? ""}
                  onChange={(e) => {
                    setCompanyId(Number(e.target.value));
                    setOffset(0);
                  }}
                  className={FILTER_INPUT + " w-full sm:w-auto"}
                >
                  {adminCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label={t("emailOutbox.filterKind")}>
                <select
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value as Kind | "");
                    setOffset(0);
                  }}
                  className={FILTER_INPUT + " w-full sm:w-auto"}
                >
                  <option value="">{t("emailOutbox.allKinds")}</option>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(KIND_LABEL[k])}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label={t("emailOutbox.filterStatus")}>
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as "" | "sent" | "failed");
                    setOffset(0);
                  }}
                  className={FILTER_INPUT + " w-full sm:w-auto"}
                >
                  <option value="">{t("emailOutbox.allStatus")}</option>
                  <option value="sent">{t("emailOutbox.statusSent")}</option>
                  <option value="failed">{t("emailOutbox.statusFailed")}</option>
                </select>
              </FilterField>
              <FilterField label={t("emailOutbox.filterSearch")} className="grow sm:max-w-md">
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setOffset(0);
                  }}
                  placeholder={t("emailOutbox.searchPh")}
                  className={FILTER_INPUT + " w-full"}
                />
              </FilterField>
            </div>
          </FilterPanel>
        </div>
        <div className="text-xs text-neutral-500">
          {showing > 0 && (
            <>
              {offset + 1}–{offset + showing} {t("emailOutbox.of")} {total}
            </>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("emailOutbox.loading")}</p>
        )}
        {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("emailOutbox.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 whitespace-nowrap">
                  {t("emailOutbox.colWhen")}
                </th>
                <th className="text-left px-3 py-2 whitespace-nowrap">
                  {t("emailOutbox.colKind")}
                </th>
                <th className="text-left px-3 py-2 whitespace-nowrap">{t("emailOutbox.colRef")}</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">{t("emailOutbox.colTo")}</th>
                <th className="text-left px-3 py-2 w-full">{t("emailOutbox.colSubject")}</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">{t("emailOutbox.colBy")}</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">
                  {t("emailOutbox.colStatus")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {data.rows.map((row) => {
                const k = (KINDS as readonly string[]).includes(row.kind)
                  ? (row.kind as Kind)
                  : "other";
                const link =
                  row.entityKind === "proforma_request" && row.entityId != null
                    ? { href: `/proformas/${row.entityId}`, label: t("emailOutbox.openProforma") }
                    : row.invoiceDossierId != null
                      ? {
                          href: `/dossiers/${row.invoiceDossierId}`,
                          label: t("emailOutbox.openDossier"),
                        }
                      : null;
                return (
                  <tr key={row.id} className="align-top">
                    <td className="px-3 py-2 text-xs text-neutral-400 whitespace-nowrap">
                      {new Date(row.occurredAt).toLocaleString("pt-BR")}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      <span className="inline-block rounded bg-neutral-700/40 text-neutral-300 px-2 py-0.5">
                        {t(KIND_LABEL[k])}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-mono text-neutral-200 whitespace-nowrap">
                        {row.entityLabel ?? "—"}
                      </div>
                      {link && (
                        <Link href={link.href} className="text-lime hover:underline">
                          {link.label}
                        </Link>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="text-neutral-200">{row.toEmail}</div>
                      {row.toName && <div className="text-neutral-500">{row.toName}</div>}
                      {row.cc && row.cc.length > 0 && (
                        <div className="text-neutral-500 break-all">
                          {t("emailOutbox.ccPre")} {row.cc.join(", ")}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="text-neutral-200">{row.subject}</div>
                      {row.customMessage && (
                        <div className="mt-1 text-neutral-400 whitespace-pre-wrap border-l-2 border-neutral-700 pl-2">
                          <span className="text-neutral-500">
                            {t("emailOutbox.customMessagePre")}
                          </span>{" "}
                          {row.customMessage}
                        </div>
                      )}
                      {row.attachments && row.attachments.length > 0 && (
                        <div className="mt-1 text-neutral-500">
                          {t("emailOutbox.attachmentsPre")}{" "}
                          {row.attachments.map((a) => a.filename).join(", ")}
                        </div>
                      )}
                      {row.source === "audit_backfill" && (
                        <div className="mt-1 text-neutral-600 italic">
                          {t("emailOutbox.backfillHint")}
                        </div>
                      )}
                      {row.error && (
                        <div className="mt-1 text-red-400">
                          {t("emailOutbox.errorPre")} {row.error}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {row.impersonatorName ? (
                        <>
                          {row.impersonatorName}{" "}
                          <span className="text-neutral-500">
                            ({t("emailOutbox.asUserPre")} {row.userName ?? "—"})
                          </span>
                        </>
                      ) : (
                        (row.userName ?? <span className="text-neutral-600">—</span>)
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {row.status === "sent" ? (
                        <span className="badge b-ok">{t("emailOutbox.statusSent")}</span>
                      ) : (
                        <span className="badge b-dang">{t("emailOutbox.statusFailed")}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          testId="emailOutbox-pagination"
        />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-7xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}
